import {
  Ban,
  CheckCircle2,
  Lock,
  Mail,
  MailWarning,
  ScrollText,
  Send,
  Users,
} from "lucide-react";
import { buildListInsights } from "@/shared/list/buildListInsights";
import type { ModuleInsightsModel } from "@/shared/ui/core/ModuleInsights";

export function sysActiveInactiveInsights(args: {
  active: number;
  inactive: number;
  distributionTitle: string;
  onClear: () => void;
  onActive: () => void;
  onInactive: () => void;
  activeLabel?: string;
  inactiveLabel?: string;
  /** `"all"` chọn thẻ Tổng; `Active` / `Inactive` chọn thẻ tương ứng. */
  selectedStatus?: string;
}): ModuleInsightsModel {
  const { active, inactive } = args;
  return buildListInsights({
    buckets: [
      { status: "Active", count: active },
      { status: "Inactive", count: inactive },
    ],
    distributionTitle: args.distributionTitle,
    selectedStatus: args.selectedStatus,
    kpis: [
      {
        id: "total",
        label: "Tổng",
        count: active + inactive,
        tone: "primary",
        icon: Users,
        onClick: args.onClear,
      },
      {
        id: "active",
        label: args.activeLabel ?? "Active",
        count: active,
        tone: "success",
        icon: CheckCircle2,
        onClick: args.onActive,
      },
      {
        id: "inactive",
        label: args.inactiveLabel ?? "Inactive",
        count: inactive,
        tone: "draft",
        icon: Ban,
        onClick: args.onInactive,
      },
    ],
  });
}

export function sysUsersInsights(args: {
  active: number;
  locked: number;
  inactive: number;
  onClear: () => void;
  onActive: () => void;
  onLocked: () => void;
  onInactive: () => void;
  activeLabel?: string;
  lockedLabel?: string;
  inactiveLabel?: string;
  selectedStatus?: string;
}): ModuleInsightsModel {
  const { active, locked, inactive } = args;
  return buildListInsights({
    buckets: [
      { status: "Active", count: active },
      { status: "Locked", count: locked },
      { status: "Inactive", count: inactive },
    ],
    distributionTitle: "Người dùng theo trạng thái",
    selectedStatus: args.selectedStatus,
    kpis: [
      {
        id: "total",
        label: "Tổng",
        count: active + locked + inactive,
        tone: "primary",
        icon: Users,
        onClick: args.onClear,
      },
      {
        id: "active",
        label: args.activeLabel ?? "Active",
        count: active,
        tone: "success",
        icon: CheckCircle2,
        onClick: args.onActive,
      },
      {
        id: "locked",
        label: args.lockedLabel ?? "Locked",
        count: locked,
        alertTone: "danger",
        icon: Lock,
        onClick: args.onLocked,
      },
      {
        id: "inactive",
        label: args.inactiveLabel ?? "Inactive",
        count: inactive,
        tone: "draft",
        icon: Ban,
        onClick: args.onInactive,
      },
    ],
  });
}

/** Audit trail — no status axis; total respects current list filters. */
export function sysAuditInsights(args: {
  total: number;
  onClear: () => void;
  selectedStatus?: string;
}): ModuleInsightsModel {
  return buildListInsights({
    buckets: [{ status: "Event", count: args.total }],
    distributionTitle: "Sự kiện audit",
    selectedStatus: args.selectedStatus ?? "all",
    kpis: [
      {
        id: "total",
        label: "Sự kiện",
        count: args.total,
        tone: "primary",
        icon: ScrollText,
        onClick: args.onClear,
      },
    ],
  });
}

export function sysEmailLogInsights(args: {
  simulated: number;
  sent: number;
  failed: number;
  onClear: () => void;
  onSimulated: () => void;
  onSent: () => void;
  onFailed: () => void;
  selectedStatus?: string;
}): ModuleInsightsModel {
  const { simulated, sent, failed } = args;
  return buildListInsights({
    buckets: [
      { status: "Simulated", count: simulated },
      { status: "Sent", count: sent },
      { status: "Failed", count: failed },
    ],
    distributionTitle: "Email theo trạng thái",
    selectedStatus: args.selectedStatus,
    kpis: [
      {
        id: "total",
        label: "Tổng",
        count: simulated + sent + failed,
        tone: "primary",
        icon: Mail,
        onClick: args.onClear,
      },
      {
        id: "simulated",
        label: "Giả lập",
        count: simulated,
        tone: "draft",
        icon: Mail,
        onClick: args.onSimulated,
      },
      {
        id: "sent",
        label: "Đã gửi",
        count: sent,
        tone: "success",
        icon: Send,
        onClick: args.onSent,
      },
      {
        id: "failed",
        label: "Thất bại",
        count: failed,
        alertTone: "danger",
        icon: MailWarning,
        onClick: args.onFailed,
      },
    ],
  });
}
